import React, { FC } from 'react';
import { useStore } from '@/components/store/Store';
import TextInput from '@/components/forms/TextInput';
import { BlockPath, useCurrentBlock } from '@/editor/PageEditor/CurrentBlockProvider';
import { useTranslation } from 'react-i18next';
import SelectInput from '@/components/forms/SelectInput';
import SwitchInput from '@/components/forms/SwitchInput';
import { useWatch } from 'react-hook-form';
import { BlockSpecificPage } from '@/editor/lib/declarations/BlockSpecificPage';
import { BannerBlock } from '@/declarations/models/blocks/BannerBlock';
import Container from '@/components/Container';
import { FormHelperText } from '@mui/material';
import Grid from '@mui/material/Grid';

export const BannerBlockAdvancedSettings: FC = () => {
  const { blockPath } = useCurrentBlock();
  const { t } = useTranslation('components');

  const includeDMSearch = useWatch<BlockSpecificPage<BannerBlock>, `${BlockPath}.dmSearch`>({
    name: `${blockPath}.dmSearch`,
  });
  const {
    state: { currentUser },
  } = useStore();
  const isSuperAdmin = currentUser?.user?.is_super_admin;

  const superAdminEffectOptions = [
    '',
    'zoom',
    'parallax',
    'effect-media-contain',
    'effect-media-contain-on-phone',
    'effect-media-contain-on-large',
  ];

  // 'parallax'
  // Could add other effects/features here like: util-img-contain / util-video-contain, util-banner-media-swap

  return (
    <Container fullWidth left column gap={2}>
      <Container column fullWidth mt={2}>
        <TextInput
          path={`${blockPath}.svgLogo`}
          label={t('BannerBlock.AdvancedSettings.svgLogo.Label')}
          helperText={t('BannerBlock.AdvancedSettings.svgLogo.HelperText')}
          multiline
          minRows={3}
          maxRows={20}
        />
        <TextInput path={`${blockPath}.cssClass`} label='CSS klasse' />
      </Container>
      {isSuperAdmin && (
        <Container fullWidth column left gap={0} mt={2} sx={{ backgroundColor: '#fbe6ff', padding: '1em' }}>
          <SelectInput
            hideNoSelectionOption
            defaultValue=''
            options={superAdminEffectOptions}
            getOptionKey={(effect) => `effect-${effect}`}
            getOptionLabel={(effect) => `${t(`BannerBlock.AdvancedSettings.effect.options.${effect}`)}`}
            path={`${blockPath}.effect`}
            label={t('BannerBlock.AdvancedSettings.effect.Label')}
          />
        </Container>
      )}

      {isSuperAdmin && (
        <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
          <SwitchInput
            fullWidth={false}
            path={`${blockPath}.dmSearch`}
            label={t('BannerBlock.AdvancedSettings.dmSearch.Label')}
          />
        </div>
      )}

      {isSuperAdmin && includeDMSearch && (
        <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
          <Container fullWidth column left gap={0} mt={2}>
            <TextInput
              path={`${blockPath}.dmQueryString.completeUrl`}
              label={t('BannerBlock.AdvancedSettings.dmSearch.completeUrl.Label')}
              placeholder={t('BannerBlock.AdvancedSettings.dmSearch.completeUrl.Placeholder')}
            />
            <FormHelperText
              sx={{
                ml: '1rem',
              }}>
              {t('BannerBlock.AdvancedSettings.dmSearch.completeUrl.HelperText')}
            </FormHelperText>
          </Container>
          <Grid container columnSpacing={1}>
            <Grid sx={{ xs: 7 }}>
              <TextInput
                path={`${blockPath}.dmSearchSettings.labelPlaceholder`}
                label={t('BannerBlock.AdvancedSettings.dmSearch.labelPlaceholder.Label')}
                placeholder={t('BannerBlock.AdvancedSettings.dmSearch.labelPlaceholder.Placeholder')}
              />
            </Grid>
            <Grid sx={{ xs: 5 }}>
              <TextInput
                path={`${blockPath}.dmSearchSettings.labelButton`}
                label={t('BannerBlock.AdvancedSettings.dmSearch.labelButton.Label')}
                placeholder={t('BannerBlock.AdvancedSettings.dmSearch.labelButton.Placeholder')}
              />
            </Grid>
          </Grid>
        </div>
      )}
    </Container>
  );
};
